// LandingPage.js
import React from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  FaHandsHelping,
  FaGraduationCap,
  FaUsers,
  FaChartLine,
  FaHeart,
  FaLightbulb,
} from "react-icons/fa";

const LandingPage = () => {
  return (
    <div className="landing-page" style={{ backgroundColor: "#f9fafb" }}>
      {/* Hero Section */}
      <section className="hero-section text-center py-5 bg-white">
        <Container className="py-5">
          <Row className="justify-content-center align-items-center">
            <Col lg={8} className="text-center mb-4">
              <img
                src="/newlogo1.jpg"
                alt="CSD WCCO Logo"
                className="mb-4"
                style={{ maxWidth: "420px", width: "100%", filter: "drop-shadow(0px 4px 10px rgba(27,58,135,0.10))" }}
              />
              <h1
                className="display-4 fw-bold mb-3"
                style={{ color: "var(--color-primary)", letterSpacing: "-1px" }}>
                Make a Difference
              </h1>
              <p className="lead text-muted mb-4 fs-5" style={{ lineHeight: "1.6" }}>
                Join WCCO and become part of a community dedicated to making a
                difference in society through impactful volunteer work.
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Link to="/signup">
                  <Button
                    size="lg"
                    className="fw-semibold px-4 py-2.5 shadow-sm"
                    style={{
                      backgroundColor: "var(--color-primary)",
                      borderColor: "var(--color-primary)",
                      borderRadius: "var(--radius-md)"
                    }}>
                    Get Started
                  </Button>
                </Link>
                <Link to="/browse">
                  <Button
                    variant="outline-primary"
                    size="lg"
                    className="fw-semibold px-4 py-2.5"
                    style={{
                      borderColor: "var(--color-primary)",
                      color: "var(--color-primary)",
                      borderRadius: "var(--radius-md)"
                    }}>
                    Browse Projects
                  </Button>
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why Join WCCO Section */}
      <section
        className="features-section py-5"
        style={{ backgroundColor: "#f9fafb" }}>
        <Container className="py-5">
          <h2 className="text-center fw-bold mb-5" style={{ color: "var(--color-primary)", letterSpacing: "-0.5px" }}>
            Why Join WCCO Volunteer Program?
          </h2>
          <Row className="g-4 justify-content-center">
            {[
              {
                icon: <FaHandsHelping size={28} />,
                title: "Create an Impact",
                description:
                  "Work on projects that directly benefit your community and make a lasting difference.",
              },
              {
                icon: <FaGraduationCap size={28} />,
                title: "Enhance Your Skills",
                description:
                  "Gain practical experience, develop leadership abilities, and build your professional portfolio.",
              },
              {
                icon: <FaUsers size={28} />,
                title: "Join a Community",
                description:
                  "Connect with passionate students and organizations who share your commitment to social change.",
              },
              {
                icon: <FaChartLine size={28} />,
                title: "Track Your Journey",
                description:
                  "Monitor your volunteer hours and reflect on your experience using our easy-to-use platform.",
              },
            ].map((feature, index) => (
              <Col key={index} md={6} lg={3}>
                <Card className="h-100 border-0 shadow-sm text-center p-4" style={{ borderRadius: "12px" }}>
                  <Card.Body>
                    <div className="mb-3 d-inline-flex p-3 rounded-circle" style={{ backgroundColor: "rgba(27,58,135,0.08)", color: "var(--color-primary)" }}>
                      {feature.icon}
                    </div>
                    <h4 className="h5 fw-bold mb-3" style={{ color: "#111827" }}>{feature.title}</h4>
                    <p className="text-muted mb-0" style={{ fontSize: "0.95rem", lineHeight: "1.5" }}>{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Impact Stats Section */}
      <section
        className="stats-section py-5"
        style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)", color: "white" }}>
        <Container className="py-4">
          <Row className="justify-content-center text-center g-4">
            {[
              { number: "1000+", label: "Active Student Volunteers" },
              { number: "50+", label: "Community Projects" },
              { number: "5000+", label: "Volunteer Hours" },
              { number: "20+", label: "Partner Organizations" },
            ].map((stat, index) => (
              <Col key={index} md={3} sm={6}>
                <div className="py-3">
                  <h3 className="h1 fw-bold mb-1" style={{ letterSpacing: "-1px" }}>{stat.number}</h3>
                  <p className="mb-0 text-white-50 fw-semibold" style={{ fontSize: "0.95rem" }}>{stat.label}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Who is WCCO? Section */}
      <section className="py-5 bg-white">
        <Container className="py-5 text-start">
          <h2
            className="text-center fw-bold mb-5"
            style={{ color: "var(--color-primary)", fontSize: "2.25rem", letterSpacing: "-0.5px" }}>
            Who is WCCO?
          </h2>
          <Row className="g-4 align-items-stretch">
            {/* Left Highlight Column */}
            <Col lg={5}>
              <Card 
                className="h-100 border-0 p-4 d-flex flex-column justify-content-center"
                style={{ 
                  backgroundColor: "rgba(42,157,143,0.05)", 
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "0 10px 30px rgba(42,157,143,0.06)"
                }}
              >
                <div className="mb-4" style={{ color: "var(--color-teal)" }}>
                  <FaHeart size={36} />
                </div>
                <h4 className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>Our Core Philosophy</h4>
                <p className="lead mb-0 text-secondary" style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
                  "Wits Citizenship and Community Outreach (WCCO) believes that no Witsie should graduate without the tools to contribute to the development of society through the proper exercise of their rights and responsibilities as a citizen."
                </p>
              </Card>
            </Col>

            {/* Right Information Blocks Column */}
            <Col lg={7}>
              <Card className="h-100 border-0 p-4" style={{ borderRadius: "16px", boxShadow: "0 10px 30px rgba(0,0,0,0.02)", backgroundColor: "#f9fafb" }}>
                <div className="d-flex mb-4 align-items-start">
                  <div className="p-3 rounded-circle me-3 bg-white shadow-sm" style={{ color: "var(--color-primary)" }}>
                    <FaUsers size={20} />
                  </div>
                  <div>
                    <h5 className="fw-bold text-dark mb-2">Active Citizenship & Service</h5>
                    <p className="text-secondary mb-0" style={{ lineHeight: "1.5", fontSize: "0.95rem" }}>
                      We promote active community service to bridge the gap between knowledge and experience, transforming theoretical awareness into impactful citizen action.
                    </p>
                  </div>
                </div>

                <div className="d-flex mb-4 align-items-start">
                  <div className="p-3 rounded-circle me-3 bg-white shadow-sm" style={{ color: "var(--color-teal)" }}>
                    <FaHandsHelping size={20} />
                  </div>
                  <div>
                    <h5 className="fw-bold text-dark mb-2">Flexible Volunteering Paths</h5>
                    <p className="text-secondary mb-0" style={{ lineHeight: "1.5", fontSize: "0.95rem" }}>
                      Support local organizations on an ongoing basis or embrace once-off opportunities for advocacy and raising awareness about vital community causes.
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start">
                  <div className="p-3 rounded-circle me-3 bg-white shadow-sm" style={{ color: "var(--color-teal-light)" }}>
                    <FaLightbulb size={20} />
                  </div>
                  <div>
                    <h5 className="fw-bold text-dark mb-2">Student-Led Initiatives</h5>
                    <p className="text-secondary mb-0" style={{ lineHeight: "1.5", fontSize: "0.95rem" }}>
                      Empowering volunteers to identify community needs, design practical solutions, and initiate their own projects to meet a variety of local needs.
                    </p>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section 
        className="cta-section text-center py-5"
        style={{ 
          background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)",
          color: "white" 
        }}
      >
        <Container className="py-5">
          <h2 className="mb-3 fw-bold display-5" style={{ letterSpacing: "-0.5px" }}>Ready to Make a Difference?</h2>
          <p className="lead text-white-50 mb-4 mx-auto fs-5" style={{ maxWidth: "600px", lineHeight: "1.6" }}>
            Start your volunteering journey with WCCO today and be part of positive change in local communities.
          </p>
          <Link to="/signup">
            <Button
              size="lg"
              className="fw-bold shadow"
              style={{
                backgroundColor: "#fff",
                color: "var(--color-primary)",
                border: "none",
                padding: "1rem 3rem",
                borderRadius: "30px",
                fontWeight: 700,
                transition: "all var(--transition)"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "scale(1.03)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(255,255,255,0.2)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              Get Started Now
            </Button>
          </Link>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 border-top bg-white">
        <Container>
          <Row className="justify-content-between align-items-center g-3">
            <Col md={4} className="text-center text-md-start">
              <p className="small text-muted mb-0">
                © 2024 WCCO. All rights reserved.
              </p>
            </Col>
            <Col md={4} className="text-center">
              <div className="d-flex justify-content-center gap-3">
                <Link to="/signup" className="text-decoration-none text-muted small fw-semibold">
                  Sign Up
                </Link>
                <Link to="/signin" className="text-decoration-none text-muted small fw-semibold">
                  Sign In
                </Link>
                <Link to="/browse" className="text-decoration-none text-muted small fw-semibold">
                  Browse Projects
                </Link>
              </div>
            </Col>
             <Col md={4} className="text-center text-md-end">
              <p className="small text-muted mb-0 fw-semibold">
                Contact: 2234099@students.wits.ac.za
              </p>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default LandingPage;
